import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Divider, Message, Panel, Stack, Tooltip, Whisper } from 'rsuite';
import { useProjectGantt, useProjects, useTaskOrderGantt, useTaskOrders } from '../hooks/useQueries';
import './ProjectGanttPage.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const LABEL_COLUMN_WIDTH = 190;
const HEADER_ROW_HEIGHT = 28;
const HEADER_HEIGHT = HEADER_ROW_HEIGHT * 2;
const ROW_HEIGHT = 38;
const BAR_HEIGHT = 22;
const MAX_EXPORT_EDGE = 16000;
const MAX_EXPORT_AREA = 256000000;

const VIEW_CONFIG = {
  year: { label: 'Year', pxPerDay: 0.8 },
  month: { label: 'Month', pxPerDay: 2.4 },
  week: { label: 'Week', pxPerDay: 6.2 },
};

const parseIsoDate = (value) => {
  const s = String(value || '').slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1);
const maxDate = (a, b) => (a.getTime() >= b.getTime() ? a : b);
const minDate = (a, b) => (a.getTime() <= b.getTime() ? a : b);
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);
const endOfYear = (date) => new Date(date.getFullYear(), 11, 31);
const startOfWeekMonday = (date) => {
  const day = date.getDay();
  const delta = day === 0 ? -6 : 1 - day;
  return addDays(date, delta);
};
const endOfWeekSunday = (date) => addDays(startOfWeekMonday(date), 6);
const startOfQuarter = (date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
};
const endOfQuarter = (date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth + 3, 0);
};

const dayDiff = (fromDate, toDate) => {
  const fromUtc = Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const toUtc = Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  return Math.round((toUtc - fromUtc) / DAY_MS);
};

const dayDiffInclusive = (fromDate, toDate) => dayDiff(fromDate, toDate) + 1;

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0h';
  if (Math.abs(n) >= 1000) return `${n.toFixed(0)}h`;
  if (Math.abs(n) >= 100) return `${n.toFixed(1)}h`;
  return `${n.toFixed(2)}h`;
};

const buildSegments = ({ rangeStart, rangeEnd, viewMode, pxPerDay }) => {
  const toSegment = (segmentStart, segmentEnd, label) => {
    const clampedStart = maxDate(segmentStart, rangeStart);
    const clampedEnd = minDate(segmentEnd, rangeEnd);
    if (clampedEnd.getTime() < clampedStart.getTime()) return null;

    return {
      label,
      start: clampedStart,
      end: clampedEnd,
      left: dayDiff(rangeStart, clampedStart) * pxPerDay,
      width: Math.max(dayDiffInclusive(clampedStart, clampedEnd) * pxPerDay, 1),
    };
  };

  const yearSegments = [];
  for (let cursor = startOfYear(rangeStart); cursor.getTime() <= rangeEnd.getTime(); cursor = new Date(cursor.getFullYear() + 1, 0, 1)) {
    const segment = toSegment(cursor, endOfYear(cursor), String(cursor.getFullYear()));
    if (segment) yearSegments.push(segment);
  }

  const detailSegments = [];
  if (viewMode === 'month') {
    for (let cursor = startOfMonth(rangeStart); cursor.getTime() <= rangeEnd.getTime(); cursor = addMonths(cursor, 1)) {
      const label = cursor.toLocaleDateString(undefined, { month: 'short' });
      const segment = toSegment(cursor, endOfMonth(cursor), label);
      if (segment) detailSegments.push(segment);
    }
  } else if (viewMode === 'week') {
    for (let cursor = startOfWeekMonday(rangeStart); cursor.getTime() <= rangeEnd.getTime(); cursor = addDays(cursor, 7)) {
      const label = cursor.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const segment = toSegment(cursor, endOfWeekSunday(cursor), label);
      if (segment) detailSegments.push(segment);
    }
  } else {
    for (let cursor = startOfQuarter(rangeStart); cursor.getTime() <= rangeEnd.getTime(); cursor = addMonths(cursor, 3)) {
      const quarter = Math.floor(cursor.getMonth() / 3) + 1;
      const label = `Q${quarter}`;
      const segment = toSegment(cursor, endOfQuarter(cursor), label);
      if (segment) detailSegments.push(segment);
    }
  }

  return { yearSegments, detailSegments };
};

const normalizeDiscipline = (raw) => {
  const taskId = Number(raw?.taskId);
  const startDate = String(raw?.startDate || '').slice(0, 10);
  const endDate = String(raw?.endDate || '').slice(0, 10);
  if (!Number.isInteger(taskId) || !parseIsoDate(startDate) || !parseIsoDate(endDate)) return null;

  return {
    type: 'discipline',
    key: `discipline-${taskId}`,
    taskId,
    taskOrderId: Number.isInteger(Number(raw?.taskOrderId)) ? Number(raw.taskOrderId) : null,
    name: String(raw?.name || raw?.taskName || `Task ${taskId}`),
    startDate,
    endDate,
    totalHours: Number(raw?.totalHours ?? 0),
    activityCount: Number(raw?.activityCount ?? 0),
  };
};

const normalizeActivity = (raw) => {
  const taskId = Number(raw?.taskId);
  const activityId = Number(raw?.activityId);
  const startDate = String(raw?.startDate || '').slice(0, 10);
  const endDate = String(raw?.endDate || '').slice(0, 10);
  if (!Number.isInteger(taskId) || !Number.isInteger(activityId) || !parseIsoDate(startDate) || !parseIsoDate(endDate)) {
    return null;
  }

  return {
    type: 'activity',
    key: `activity-${taskId}-${activityId}`,
    taskId,
    activityId,
    name: String(raw?.activityName || raw?.name || `Activity ${activityId}`),
    startDate,
    endDate,
    totalHours: Number(raw?.totalHours ?? 0),
  };
};

const compareByDateAndName = (a, b) => {
  if (a.startDate !== b.startDate) return a.startDate.localeCompare(b.startDate);
  if (a.endDate !== b.endDate) return a.endDate.localeCompare(b.endDate);
  return String(a.name || '').localeCompare(String(b.name || ''));
};

function TruncatedTextWithTooltip({ className, displayText, tooltipText, placement = 'topStart' }) {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;

    const recompute = () => {
      const next = node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight;
      setIsTruncated(next);
    };

    recompute();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(recompute);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [displayText]);

  const textNode = (
    <span ref={textRef} className={className}>
      {displayText}
    </span>
  );

  if (!isTruncated) return textNode;

  return (
    <Whisper
      placement={placement}
      trigger="hover"
      speaker={<Tooltip>{tooltipText || displayText}</Tooltip>}
    >
      {textNode}
    </Whisper>
  );
}

export default function ProjectGanttPage() {
  const params = useParams();
  const navigate = useNavigate();

  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);
  const isTaskOrderScope = Number.isInteger(taskOrderId) && taskOrderId > 0;

  const { data: projects = [] } = useProjects({
    enabled: Number.isInteger(projectId) && projectId > 0,
  });

  const { data: taskOrdersData = [] } = useTaskOrders(projectId, {
    enabled: Number.isInteger(projectId) && projectId > 0 && isTaskOrderScope,
  });

  const [viewMode, setViewMode] = useState('month');
  const [collapsedTaskIds, setCollapsedTaskIds] = useState(() => new Set());

  const projectQuery = useProjectGantt(projectId, {
    enabled: Number.isInteger(projectId) && projectId > 0 && !isTaskOrderScope,
  });
  const taskOrderQuery = useTaskOrderGantt(projectId, taskOrderId, {
    enabled: Number.isInteger(projectId) && projectId > 0 && isTaskOrderScope,
  });

  const activeQuery = isTaskOrderScope ? taskOrderQuery : projectQuery;

  const projectName = useMemo(() => {
    const project = (Array.isArray(projects) ? projects : []).find((p) => Number(p?.id) === projectId);
    return project?.name ? String(project.name) : null;
  }, [projects, projectId]);

  const taskOrderName = useMemo(() => {
    if (!isTaskOrderScope) return null;
    const taskOrders = Array.isArray(taskOrdersData) ? taskOrdersData : [];
    const taskOrder = taskOrders.find((o) => Number(o?.id) === taskOrderId);
    return taskOrder?.name ? String(taskOrder.name) : null;
  }, [taskOrdersData, taskOrderId, isTaskOrderScope]);

  const pageTitle = isTaskOrderScope ? 'Task Order Gantt' : 'Project Gantt';

  const normalized = useMemo(() => {
    const data = activeQuery?.data || {};
    const disciplines = (Array.isArray(data?.disciplines) ? data.disciplines : [])
      .map(normalizeDiscipline)
      .filter(Boolean)
      .sort(compareByDateAndName);

    const activities = (Array.isArray(data?.activities) ? data.activities : [])
      .map(normalizeActivity)
      .filter(Boolean)
      .sort(compareByDateAndName);

    let minStart = null;
    let maxEnd = null;

    const summaryStart = String(data?.summary?.windowStart || '').slice(0, 10);
    const summaryEnd = String(data?.summary?.windowEnd || '').slice(0, 10);
    if (parseIsoDate(summaryStart)) minStart = summaryStart;
    if (parseIsoDate(summaryEnd)) maxEnd = summaryEnd;

    for (const row of disciplines) {
      if (!minStart || row.startDate < minStart) minStart = row.startDate;
      if (!maxEnd || row.endDate > maxEnd) maxEnd = row.endDate;
    }

    return {
      scope: String(data?.scope || (isTaskOrderScope ? 'task-order' : 'project')),
      disciplines,
      activities,
      minStart,
      maxEnd,
    };
  }, [activeQuery?.data, isTaskOrderScope]);

  const activitiesByTask = useMemo(() => {
    const map = new Map();
    for (const activity of normalized.activities) {
      if (!map.has(activity.taskId)) map.set(activity.taskId, []);
      map.get(activity.taskId).push(activity);
    }
    return map;
  }, [normalized.activities]);

  const rows = useMemo(() => {
    if (!isTaskOrderScope) return normalized.disciplines;

    const result = [];
    for (const discipline of normalized.disciplines) {
      result.push(discipline);
      if (collapsedTaskIds.has(discipline.taskId)) continue;
      const children = (activitiesByTask.get(discipline.taskId) || []).slice().sort(compareByDateAndName);
      for (const child of children) result.push(child);
    }

    return result;
  }, [normalized.disciplines, activitiesByTask, collapsedTaskIds, isTaskOrderScope]);

  const leftRailGroups = useMemo(() => {
    const groups = [];
    let current = null;

    for (const row of rows) {
      if (row.type === 'discipline') {
        if (current) groups.push(current);

        current = {
          key: row.key,
          taskId: row.taskId,
          name: row.name,
          startDate: row.startDate,
          endDate: row.endDate,
          isCollapsed: isTaskOrderScope ? collapsedTaskIds.has(row.taskId) : false,
          rowCount: 1,
        };
        continue;
      }

      if (current && row.type === 'activity' && row.taskId === current.taskId) {
        current.rowCount += 1;
      }
    }

    if (current) groups.push(current);
    return groups;
  }, [rows, collapsedTaskIds, isTaskOrderScope]);

  const range = useMemo(() => {
    const start = parseIsoDate(normalized.minStart);
    const end = parseIsoDate(normalized.maxEnd);
    if (!start || !end || end.getTime() < start.getTime()) return null;

    const rangeStart = startOfMonth(start);
    const rangeEnd = endOfMonth(end);
    if (rangeEnd.getTime() < rangeStart.getTime()) return null;

    return { rangeStart, rangeEnd };
  }, [normalized.minStart, normalized.maxEnd]);

  const pxPerDay = VIEW_CONFIG[viewMode]?.pxPerDay ?? VIEW_CONFIG.month.pxPerDay;
  const totalDays = range ? dayDiffInclusive(range.rangeStart, range.rangeEnd) : 0;
  const computedTimelineWidth = Math.max(Math.ceil(totalDays * pxPerDay), 1);
  const timelineWidth = Math.max(computedTimelineWidth, 720);

  const segments = useMemo(() => {
    if (!range) return { yearSegments: [], detailSegments: [] };
    return buildSegments({
      rangeStart: range.rangeStart,
      rangeEnd: range.rangeEnd,
      viewMode,
      pxPerDay,
    });
  }, [range, viewMode, pxPerDay]);

  const toggleTaskCollapse = (taskId) => {
    setCollapsedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const collapseAll = () => {
    setCollapsedTaskIds(new Set(normalized.disciplines.map((d) => d.taskId)));
  };

  const expandAll = () => {
    setCollapsedTaskIds(new Set());
  };

  const goBack = () => {
    if (isTaskOrderScope) {
      navigate(`/projects/${projectId}/task-orders/${taskOrderId}`);
      return;
    }
    navigate(`/projects/${projectId}`);
  };

  const openActivityForecast = (row) => {
    if (!row || row.type !== 'activity') return;

    if (isTaskOrderScope) {
      navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${row.taskId}/activities/${row.activityId}/forecast`);
      return;
    }

    navigate(`/tasks/${row.taskId}/activities/${row.activityId}/forecast`);
  };

  const handleDownloadPng = () => {
    if (!range || rows.length === 0) return;

    const exportWidth = LABEL_COLUMN_WIDTH + timelineWidth + 1;
    const exportHeight = HEADER_HEIGHT + (rows.length * ROW_HEIGHT) + 1;

    // Export the full timeline+rows, then scale down only if needed to stay below canvas limits.
    const preferredScale = exportWidth > 5000 ? 1 : 2;
    const edgeScale = Math.min(1, MAX_EXPORT_EDGE / exportWidth, MAX_EXPORT_EDGE / exportHeight);
    const areaScale = Math.min(1, Math.sqrt(MAX_EXPORT_AREA / Math.max(exportWidth * exportHeight, 1)));
    let scale = Math.min(preferredScale, edgeScale, areaScale);
    if (!Number.isFinite(scale) || scale <= 0) scale = 1;

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(exportWidth * scale);
    canvas.height = Math.ceil(exportHeight * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportWidth, exportHeight);

    ctx.fillStyle = '#f6f8fa';
    ctx.fillRect(0, 0, LABEL_COLUMN_WIDTH, HEADER_HEIGHT);
    ctx.fillRect(LABEL_COLUMN_WIDTH, 0, timelineWidth, HEADER_HEIGHT);

    // Header bands: make year/detail rows explicit so exported markers match the UI.
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(LABEL_COLUMN_WIDTH, 0, timelineWidth, HEADER_ROW_HEIGHT);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(LABEL_COLUMN_WIDTH, HEADER_ROW_HEIGHT, timelineWidth, HEADER_ROW_HEIGHT);

    ctx.strokeStyle = '#d5d9de';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(LABEL_COLUMN_WIDTH + 0.5, 0);
    ctx.lineTo(LABEL_COLUMN_WIDTH + 0.5, exportHeight);
    ctx.moveTo(0, HEADER_ROW_HEIGHT + 0.5);
    ctx.lineTo(exportWidth, HEADER_ROW_HEIGHT + 0.5);
    ctx.moveTo(0, HEADER_HEIGHT + 0.5);
    ctx.lineTo(exportWidth, HEADER_HEIGHT + 0.5);
    ctx.stroke();

    ctx.font = '700 12px Segoe UI, Arial, sans-serif';
    ctx.fillStyle = '#1f2937';
    ctx.textBaseline = 'middle';

    for (const segment of segments.yearSegments) {
      const x = LABEL_COLUMN_WIDTH + segment.left;
      ctx.strokeStyle = '#d5d9de';
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, exportHeight);
      ctx.stroke();

      const yearTextWidth = ctx.measureText(segment.label).width;
      const yearTextX = x + Math.max((segment.width - yearTextWidth) / 2, 6);
      ctx.fillStyle = '#1f2937';
      ctx.fillText(segment.label, yearTextX, HEADER_ROW_HEIGHT / 2);
    }

    for (const segment of segments.detailSegments) {
      const x = LABEL_COLUMN_WIDTH + segment.left;
      ctx.strokeStyle = '#ebedf0';
      ctx.beginPath();
      ctx.moveTo(x + 0.5, HEADER_ROW_HEIGHT);
      ctx.lineTo(x + 0.5, exportHeight);
      ctx.stroke();

      ctx.font = '600 11px Segoe UI, Arial, sans-serif';
      ctx.fillStyle = '#475569';
      ctx.fillText(segment.label, x + 6, HEADER_ROW_HEIGHT + (HEADER_ROW_HEIGHT / 2));
    }

    ctx.font = '700 12px Segoe UI, Arial, sans-serif';
    ctx.fillStyle = '#111827';
    ctx.fillText('Discipline', 12, HEADER_HEIGHT / 2);

    // Left rail labels: discipline-only (no activity names).
    let groupRowOffset = 0;
    for (const group of leftRailGroups) {
      const groupTop = HEADER_HEIGHT + (groupRowOffset * ROW_HEIGHT);
      const labelY = groupTop + (ROW_HEIGHT / 2);

      ctx.font = '700 12px Segoe UI, Arial, sans-serif';
      ctx.fillStyle = '#0f172a';
      ctx.fillText(group.name, 12, labelY);

      groupRowOffset += Number(group.rowCount || 1);
    }

    rows.forEach((row, index) => {
      const rowTop = HEADER_HEIGHT + (index * ROW_HEIGHT);
      const barTop = rowTop + ((ROW_HEIGHT - BAR_HEIGHT) / 2);

      ctx.strokeStyle = '#f1f3f5';
      ctx.beginPath();
      ctx.moveTo(0, rowTop + 0.5);
      ctx.lineTo(exportWidth, rowTop + 0.5);
      ctx.stroke();

      const startDate = parseIsoDate(row.startDate);
      const endDate = parseIsoDate(row.endDate);
      if (!startDate || !endDate) return;

      const left = LABEL_COLUMN_WIDTH + (dayDiff(range.rangeStart, startDate) * pxPerDay);
      const width = Math.max(dayDiffInclusive(startDate, endDate) * pxPerDay, 2);

      ctx.fillStyle = row.type === 'discipline' ? '#1f5d8a' : '#3d87b3';
      ctx.fillRect(left, barTop, width, BAR_HEIGHT);

      ctx.fillStyle = '#ffffff';
      ctx.font = row.type === 'discipline'
        ? '600 11px Segoe UI, Arial, sans-serif'
        : '400 11px Segoe UI, Arial, sans-serif';

      const text = row.type === 'discipline'
        ? `${row.name} (${formatHours(row.totalHours)})`
        : row.name;

      if (row.type === 'discipline') {
        const textWidth = ctx.measureText(text).width;
        const centeredX = left + Math.max((width - textWidth) / 2, 6);
        ctx.fillText(text, centeredX, barTop + (BAR_HEIGHT / 2));
      } else {
        ctx.fillText(text, left + 6, barTop + (BAR_HEIGHT / 2));
      }
    });

    const filenameScope = isTaskOrderScope ? `task-order-${taskOrderId}` : 'project';
    const filename = `gantt-${projectId}-${filenameScope}.png`;

    const triggerDownload = (blobUrl) => {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    };

    canvas.toBlob((blob) => {
      if (blob) {
        triggerDownload(URL.createObjectURL(blob));
        return;
      }
      const fallbackUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = fallbackUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 'image/png');
  };

  if (!Number.isInteger(projectId) || projectId <= 0) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page ppc-gantt-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <div className="ppc-gantt-header-main">
          <div className="ppc-gantt-crumbs">
            <button type="button" className="ppc-gantt-crumb-link" onClick={() => navigate('/')}>
              Projects
            </button>

            <i className="bi bi-chevron-right ppc-gantt-crumb-sep" aria-hidden="true" />

            <button
              type="button"
              className="ppc-gantt-crumb-link"
              onClick={() => navigate(`/projects/${projectId}`)}
            >
              {projectName || `Project ${projectId}`}
            </button>

            {isTaskOrderScope ? (
              <>
                <i className="bi bi-chevron-right ppc-gantt-crumb-sep" aria-hidden="true" />
                <button
                  type="button"
                  className="ppc-gantt-crumb-link"
                  onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}`)}
                >
                  {taskOrderName || `Task order ${taskOrderId}`}
                </button>
              </>
            ) : null}

            <i className="bi bi-chevron-right ppc-gantt-crumb-sep" aria-hidden="true" />
            <span className="ppc-gantt-crumb-current">Gantt</span>
          </div>

          <h3 style={{ margin: 0, lineHeight: '22px' }}>
            <span className="ppc-gantt-title">
              <i className="bi bi-bar-chart-steps ppc-gantt-title-icon" aria-hidden="true" />
              <span>{pageTitle}</span>
            </span>
          </h3>
        </div>
        <Stack spacing={10} wrap>
          <button type="button" className="ppc-gantt-btn" onClick={goBack}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back
          </button>
          {isTaskOrderScope ? (
            <button
              type="button"
              className="ppc-gantt-btn"
              onClick={() => navigate(`/projects/${projectId}/gantt`)}
            >
              <i className="bi bi-bar-chart-steps" aria-hidden="true" />
              Project view
            </button>
          ) : null}
        </Stack>
      </Stack>

      <Divider />

      <Panel bordered className="ppc-gantt-toolbar-panel">
        <Stack justifyContent="space-between" alignItems="center" wrap>
          <div className="ppc-gantt-segment" role="group" aria-label="Timeline zoom level">
            {Object.entries(VIEW_CONFIG).map(([value, cfg]) => (
              <button
                type="button"
                key={value}
                className={`ppc-gantt-btn ppc-gantt-btn--sm${viewMode === value ? ' ppc-gantt-btn--primary' : ''}`}
                onClick={() => setViewMode(value)}
                aria-pressed={viewMode === value}
              >
                {cfg.label}
              </button>
            ))}
          </div>

          <Stack spacing={8} wrap>
            {isTaskOrderScope ? (
              <>
                <button type="button" className="ppc-gantt-btn ppc-gantt-btn--sm" onClick={expandAll}>Expand all</button>
                <button type="button" className="ppc-gantt-btn ppc-gantt-btn--sm" onClick={collapseAll}>Collapse all</button>
              </>
            ) : null}
            <button
              type="button"
              className="ppc-gantt-btn ppc-gantt-btn--sm"
              onClick={handleDownloadPng}
              disabled={!range || rows.length === 0}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-image" aria-hidden="true" />
                Export image
              </span>
            </button>
          </Stack>
        </Stack>
      </Panel>

      {activeQuery.isLoading ? (
        <Panel bordered className="ppc-gantt-empty-panel">Loading timeline...</Panel>
      ) : null}

      {activeQuery.error ? (
        <Message type="error" showIcon>
          {String(activeQuery.error?.message || 'Failed to load Gantt data.')}
        </Message>
      ) : null}

      {!activeQuery.isLoading && !activeQuery.error && (!range || rows.length === 0) ? (
        <Panel bordered className="ppc-gantt-empty-panel">
          No forecasted activities with non-zero monthly hours were found for this scope.
        </Panel>
      ) : null}

      {!activeQuery.isLoading && !activeQuery.error && range && rows.length > 0 ? (
        <div className="ppc-gantt-board" role="region" aria-label="Project Gantt board">
          <div className="ppc-gantt-label-column">
            <div className="ppc-gantt-label-header">Discipline</div>
            {leftRailGroups.map((group) => {
              const subtitle = `${formatIsoDate(parseIsoDate(group.startDate)) || group.startDate} to ${formatIsoDate(parseIsoDate(group.endDate)) || group.endDate}`;

              return (
                <div
                  key={group.key}
                  className="ppc-gantt-label-group"
                  style={{ height: Math.max(group.rowCount * ROW_HEIGHT, ROW_HEIGHT) }}
                >
                  <div className="ppc-gantt-label-group-row">
                    {isTaskOrderScope ? (
                      <button
                        type="button"
                        className="ppc-gantt-collapse-toggle"
                        onClick={() => toggleTaskCollapse(group.taskId)}
                        aria-label={group.isCollapsed ? 'Expand discipline' : 'Collapse discipline'}
                      >
                        {group.isCollapsed ? '+' : '-'}
                      </button>
                    ) : null}
                    <div className="ppc-gantt-label-text" title={`${group.name} | ${subtitle}`}>
                      {group.name}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ppc-gantt-timeline-scroll">
            <div className="ppc-gantt-timeline-canvas" style={{ width: timelineWidth }}>
              <div className="ppc-gantt-header" style={{ height: HEADER_HEIGHT }}>
                <div className="ppc-gantt-header-row" style={{ height: HEADER_ROW_HEIGHT }}>
                  {segments.yearSegments.map((segment) => (
                    <div
                      key={`year-${segment.label}-${segment.left}`}
                      className="ppc-gantt-header-cell year"
                      style={{ left: segment.left, width: segment.width }}
                      title={segment.label}
                    >
                      {segment.label}
                    </div>
                  ))}
                </div>

                <div className="ppc-gantt-header-row" style={{ top: HEADER_ROW_HEIGHT, height: HEADER_ROW_HEIGHT }}>
                  {segments.detailSegments.map((segment) => (
                    <div
                      key={`detail-${segment.label}-${segment.left}`}
                      className="ppc-gantt-header-cell detail"
                      style={{ left: segment.left, width: segment.width }}
                      title={segment.label}
                    >
                      {segment.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className="ppc-gantt-body" style={{ height: rows.length * ROW_HEIGHT }}>
                {segments.detailSegments.map((segment) => (
                  <div
                    key={`grid-${segment.left}`}
                    className="ppc-gantt-grid-line"
                    style={{ left: segment.left }}
                  />
                ))}

                {rows.map((row, rowIndex) => {
                  const startDate = parseIsoDate(row.startDate);
                  const endDate = parseIsoDate(row.endDate);
                  if (!startDate || !endDate) return null;

                  const barLeft = dayDiff(range.rangeStart, startDate) * pxPerDay;
                  const barWidth = Math.max(dayDiffInclusive(startDate, endDate) * pxPerDay, 2);
                  const barTop = rowIndex * ROW_HEIGHT + ((ROW_HEIGHT - BAR_HEIGHT) / 2);
                  const clickable = row.type === 'activity';

                  return (
                    <div
                      key={row.key}
                      className={`ppc-gantt-bar ${row.type} ${clickable ? 'clickable' : ''}`}
                      style={{ left: barLeft, width: barWidth, top: barTop, height: BAR_HEIGHT }}
                      onClick={clickable ? () => openActivityForecast(row) : undefined}
                      title={row.type === 'discipline'
                        ? `${row.name} | ${row.startDate} to ${row.endDate} | ${formatHours(row.totalHours)}`
                        : `${row.name} | ${row.startDate} to ${row.endDate}`}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : -1}
                      onKeyDown={clickable ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openActivityForecast(row);
                        }
                      } : undefined}
                    >
                      {row.type === 'discipline' ? (
                        <span className="ppc-gantt-bar-label">
                          {`${row.name} (${formatHours(row.totalHours)})`}
                        </span>
                      ) : (
                        <TruncatedTextWithTooltip
                          className="ppc-gantt-bar-label"
                          displayText={row.name}
                          tooltipText={row.name}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
